import { HttpClient, HttpEvent, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';
import { map, retryWhen, switchMap } from 'rxjs/operators';

import { EnvironmentConfigService, ShAbstractHttpService } from '@stylehaven/components';
import { genericRetryStrategy, RetryStrategyOptions } from '../../helpers/generic-retry-strategy.helper';
import { UploadRequestQueue } from './request-queue.service';

@Injectable({
    providedIn: 'root'
})
export class HttpService implements ShAbstractHttpService {
    private readonly apiUrl$: Observable<string>;
    private readonly READ_RETRY_OPTIONS: RetryStrategyOptions = {
        excludedStatusCodes: [500, 400, 403, 409],
        scalingDuration: 500,
        maxRetryAttempts: 2
    };

    private readonly WRITE_RETRY_OPTIONS: RetryStrategyOptions = {
        excludedStatusCodes: [500, 400, 404, 403, 409],
        scalingDuration: 500,
        maxRetryAttempts: 1
    };

    constructor(
        protected readonly http: HttpClient,
        protected readonly configService: EnvironmentConfigService,
        protected readonly queue: UploadRequestQueue
    ) {
        this.apiUrl$ = this.configService.get('apiUrl');
    }

    request<T>(endpoint: string, method: string, body: any): Observable<T> {
        return this.apiUrl$.pipe(switchMap(apiUrl => this.http.request<T>(method, `${apiUrl}${endpoint}`, { body })));
    }

    get<T>(
        endpoint: string,
        params?: HttpParams,
        headers: HttpHeaders = new HttpHeaders(),
        retry: boolean = false
    ): Observable<T> {
        return this.apiUrl$.pipe(
            switchMap(apiUrl => {
                const request: Observable<T> = this.http.get<T>(`${apiUrl}${endpoint}`, { params, headers });

                return retry ? request.pipe(retryWhen(genericRetryStrategy(this.READ_RETRY_OPTIONS))) : request;
            })
        );
    }

    getText(endpoint: string, params?: HttpParams): Observable<string> {
        return this.apiUrl$.pipe(
            switchMap(apiUrl =>
                this.http
                    .get(`${apiUrl}${endpoint}`, { params, responseType: 'text' as const })
                    .pipe(retryWhen(genericRetryStrategy(this.READ_RETRY_OPTIONS)))
            )
        );
    }

    getBlob(endpoint: string): Observable<Blob> {
        return this.apiUrl$.pipe(
            switchMap(apiUrl =>
                this.http.get<Blob>(`${apiUrl}${endpoint}`, {
                    responseType: 'blob' as 'json'
                })
            )
        );
    }

    post<T>(
        endpoint: string,
        data: any,
        headers: HttpHeaders = new HttpHeaders(),
        params: HttpParams = new HttpParams(),
        retry: boolean = false
    ): Observable<T> {
        return this.apiUrl$.pipe(
            switchMap(apiUrl => {
                const request: Observable<T> = this.http.post<T>(`${apiUrl}${endpoint}`, data, { headers, params });

                return retry ? request.pipe(retryWhen(genericRetryStrategy(this.READ_RETRY_OPTIONS))) : request;
            })
        );
    }

    postProgress<T>(endpoint: string, data: any): Observable<HttpEvent<T>> {
        // Since the queue is detached from this observable, we can't use retries (in the normal way)
        return this.apiUrl$.pipe(switchMap(apiUrl => this.queue.invoke<T>(`${apiUrl}${endpoint}`, data)));
    }

    postResponse<T>(
        endpoint: string,
        data: any,
        headers: HttpHeaders = new HttpHeaders(),
        params: HttpParams = new HttpParams()
    ): Observable<HttpResponse<T>> {
        return this.apiUrl$.pipe(
            switchMap(apiUrl =>
                this.http
                    .post<T>(`${apiUrl}${endpoint}`, data, { headers, params, observe: 'response' })
                    .pipe(retryWhen(genericRetryStrategy(this.READ_RETRY_OPTIONS)))
            )
        );
    }

    put<T>(
        endpoint: string,
        data: any,
        headers: HttpHeaders = new HttpHeaders(),
        params: HttpParams = new HttpParams()
    ): Observable<T> {
        return this.apiUrl$.pipe(
            switchMap(apiUrl =>
                this.http
                    .put<T>(`${apiUrl}${endpoint}`, data, { headers, params })
                    .pipe(retryWhen(genericRetryStrategy(this.WRITE_RETRY_OPTIONS)))
            )
        );
    }

    patch<T>(endpoint: string, data: any, headers: HttpHeaders = new HttpHeaders()): Observable<T> {
        return this.apiUrl$.pipe(
            switchMap(apiUrl =>
                this.http
                    .patch<T>(`${apiUrl}${endpoint}`, data, { headers })
                    .pipe(retryWhen(genericRetryStrategy(this.WRITE_RETRY_OPTIONS)))
            )
        );
    }

    patchResponse<T>(
        endpoint: string,
        data: any,
        headers: HttpHeaders = new HttpHeaders()
    ): Observable<HttpResponse<T>> {
        return this.apiUrl$.pipe(
            switchMap(apiUrl =>
                this.http
                    .patch<T>(`${apiUrl}${endpoint}`, data, { headers, observe: 'response' })
                    .pipe(retryWhen(genericRetryStrategy(this.WRITE_RETRY_OPTIONS)))
            )
        );
    }

    delete<T>(endpoint: string, data?: any): Observable<HttpEvent<T>> {
        return this.apiUrl$.pipe(
            switchMap(apiUrl =>
                this.http
                    .delete<T>(`${apiUrl}${endpoint}`, data)
                    .pipe(retryWhen(genericRetryStrategy(this.WRITE_RETRY_OPTIONS)))
            )
        );
    }

    head(endpoint: string, params?: HttpParams, headers?: HttpHeaders): Observable<HttpResponse<void>> {
        return this.apiUrl$.pipe(
            switchMap(apiUrl =>
                this.http
                    .head<void>(`${apiUrl}${endpoint}`, { params, observe: 'response', headers })
                    .pipe(retryWhen(genericRetryStrategy(this.READ_RETRY_OPTIONS)))
            )
        );
    }

    count(endpoint: string, params?: HttpParams): Observable<number> {
        return this.head(endpoint, params).pipe(
            map((response: HttpResponse<void>) => response.headers.get('Result-Count')!),
            map((countAsString: string) => Number.parseInt(countAsString))
        );
    }

    getExternalBlob(url: string): Observable<Blob> {
        return this.apiUrl$.pipe(
            switchMap(() =>
                this.http.get<Blob>(url, {
                    responseType: 'blob' as 'json'
                })
            )
        );
    }
}
